import './promise-compat';
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { ToastrModule } from 'ngx-toastr';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication } from '@angular/platform-browser';
import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeEnExtra from '@angular/common/locales/extra/en';
import { CustomErrorHandler } from './app/common/custom-error.handler';
import { AppRoutingModule } from './app/app-routing.module';
import { MatDialogModule } from '@angular/material/dialog';
import { authenticationInterceptor } from './app/common/auth/authenticationInterceptor';
import { dedupeGetInterceptor } from './app/common/auth/dedupe-get.interceptor';
import { loadSocialAuthConfig } from './app/common/auth/load-social-auth.config';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { loaderInterceptor } from './app/common/auth/loader.interceptor';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { versionInterceptor } from './app/common/auth/version.interceptor';
import { uxHttpEventInterceptor } from './app/common/ux/ux-http-event.interceptor';
import { UxRecorderService } from './app/common/ux/ux-recorder.service';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { SOCIAL_AUTH_CONFIG, SocialLoginModule } from '@abacritt/angularx-social-login';
import * as Sentry from '@sentry/angular';
import { gitVersion } from './version';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);
registerLocaleData(localeEn, 'en-US', localeEnExtra);

if (environment.production) {
  enableProdMode();
}

(window as any)['pdfWorkerSrc4.8.69'] = '/assets/pdf.worker.4.8.69.entry.mjs';

// Disable Sentry in local development and e2e test environments
const isTestEnvironment = ['localhost', '127.0.0.1', 'backend'].includes(location.hostname);
if (environment.sentryDsn && !isTestEnvironment) {
  Sentry.init({
    dsn: environment.sentryDsn,
    tunnel: '/api/stats/sentry',
    release: gitVersion,
    sendDefaultPii: true,
    // One release-health session per page load. The default lifecycle 'route' starts a new session
    // on every history change, and the SDK's history instrumentation starts with lastHref undefined —
    // so the Angular router's initial replaceState onto the SAME url ended the fresh session after
    // ~0.6 s and opened a second one: two tunnel POSTs and two sessions for every page load.
    integrations: [Sentry.browserSessionIntegration({ lifecycle: 'page' })],
    // Session replay deliberately not enabled: it shipped customer content unmasked to a third
    // party. Replay is done in-house (openspec/changes/ux-session-replay).
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      SocialLoginModule,
      LoadingBarModule,
      ToastrModule.forRoot({
        preventDuplicates: true,
        progressBar: true,
        timeOut: 2000,
        enableHtml: true,
        positionClass: 'toast-bottom-center',
      }),
      MatDialogModule,
      MatNativeDateModule,
      AppRoutingModule
    ),
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    { provide: LOCALE_ID, useValue: 'en-US' },
    { provide: SOCIAL_AUTH_CONFIG, useFactory: loadSocialAuthConfig, deps: [HttpClient] },
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', subscriptSizing: 'dynamic' } },
    // usePopover:false is LOAD-BEARING for ux-session-replay: rrweb 2.1.1 cannot record
    // Popover-API overlays — with the CDK default every dialog/select/menu replays as display:none.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideHttpClient(
      withFetch(),
      // dedupeGetInterceptor must stay FIRST — see its doc comment.
      withInterceptors([
        dedupeGetInterceptor,
        authenticationInterceptor,
        loaderInterceptor,
        versionInterceptor,
        uxHttpEventInterceptor,
      ])
    ),
    provideAppInitializer(() => inject(UxRecorderService).init()),
    provideAnimations(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
      fallbackLang: 'en',
      lang: 'en',
    }),
  ],
})
  .then()
  .catch((err) => console.log(err));
